import React, { useMemo } from 'react';
import { Link } from './Link';
import { JC_COOKIE_CONSENT_OPEN_EVENT } from '../utils/cookie-consent/constants';
import { usePageContext } from 'vike-react/usePageContext';
import type { FooterStaticStrings } from '../utils/footer/buildFooterStaticStrings';
import { buildFooterStaticStrings } from '../utils/footer/buildFooterStaticStrings';
import { useFooterStrings } from '../contexts/FooterStringsContext';
import { loadLocaleData } from '../utils/routing/url-helpers';
import { resolvePageLocale } from '../utils/routing/resolve-page-locale';
import { FooterSocialMediaBar } from './FooterSocialMediaBar';
import {
  buildJobSearchGuideFooterLinks,
  buildJuniorsFooterLink,
  buildEmployerScoreFooterLink,
} from '../utils/seo/indexation-discovery-links';

/** TechTactics Oy Finnish business identifier (Y-tunnus), shown inline per Information Society Code 917/2014. */
const BUSINESS_ID = '3339498-2';

interface FooterLinkItem {
  href: string;
  label: string;
  /** Rendered bold/emphasised (the two hub "→" links at the end of Find jobs). */
  emphasis?: boolean;
  /** Render as a plain unprefixed <a> instead of locale-aware <Link> (English-only utility pages). */
  plain?: boolean;
}

interface FooterColumn {
  key: string;
  heading: string;
  items: FooterLinkItem[];
}

function FooterColumnLinks({
  items,
  locale,
  linkClassName,
  emphasisClassName,
}: {
  items: FooterLinkItem[];
  locale: string;
  linkClassName: string;
  emphasisClassName: string;
}) {
  return (
    <ul className="space-y-2">
      {items.map((item) => (
        <li key={item.href}>
          {item.plain ? (
            <a href={item.href} className={item.emphasis ? emphasisClassName : linkClassName}>
              {item.label}
              {item.emphasis ? ' →' : ''}
            </a>
          ) : (
            <Link href={item.href} locale={locale} className={item.emphasis ? emphasisClassName : linkClassName}>
              {item.label}
              {item.emphasis ? ' →' : ''}
            </Link>
          )}
        </li>
      ))}
    </ul>
  );
}

function FooterContent({
  locale,
  strings,
  bleedMargins = false,
}: {
  locale: string;
  strings: FooterStaticStrings;
  /** When true, apply negative horizontal margins for edge-to-edge chrome inside padded columns. Avoid when any ancestor uses overflow-x-hidden (e.g. SearchWrapper, SRP scroll) or the footer will clip. Default false. */
  bleedMargins?: boolean;
}) {
  const normalizedLocale = locale.toLowerCase().split('-')[0];
  const discoveryLocale =
    normalizedLocale === 'fi' || normalizedLocale === 'sv' ? normalizedLocale : 'en';

  const guideLinks = buildJobSearchGuideFooterLinks(discoveryLocale);
  const juniorsLink = buildJuniorsFooterLink(discoveryLocale);
  const employerScoreLink = buildEmployerScoreFooterLink(discoveryLocale);

  const columns: FooterColumn[] = [
    {
      key: 'findJobs',
      heading: strings.colFindJobs,
      items: [
        { href: '/jobs', label: strings.openJobsInFinland },
        { href: '/jobs/location/helsinki_finland', label: strings.jobsInHelsinki },
        { href: '/jobs/location/tampere_finland', label: strings.jobsInTampere },
        { href: '/jobs/location/espoo_finland', label: strings.jobsInEspoo },
        { href: '/jobs/location/turku_finland', label: strings.jobsInTurku },
        { href: '/jobs/location/oulu_finland', label: strings.jobsInOulu },
        { href: '/jobs/location/remote_global', label: strings.remoteJobs },
        { href: '/locations', label: strings.allLocations, emphasis: true },
        { href: '/sitemap', label: strings.browseAllPages, emphasis: true },
      ],
    },
    {
      key: 'jobSeekers',
      heading: strings.colJobSeekers,
      items: [
        { href: '/salary', label: strings.salariesByRole },
        { href: '/best-companies', label: strings.bestEmployers },
        ...guideLinks,
        juniorsLink,
        { href: '/collective-agreements', label: strings.collectiveAgreementsLink },
        { href: '/resources', label: strings.guidesAndArticles },
        { href: '/auth/login', label: strings.signIn, plain: true },
      ],
    },
    {
      key: 'company',
      heading: strings.colCompany,
      items: [
        { href: '/about', label: strings.aboutJobcrawls },
        { href: '/contact', label: strings.contact },
        { href: '/editorial-standards', label: strings.editorialStandards },
        employerScoreLink,
        { href: '/partnerships', label: strings.partnerships },
        { href: '/bot', label: strings.howCrawlerWorks },
      ],
    },
    {
      key: 'employersAndDevelopers',
      heading: strings.colEmployersAndDevelopers,
      items: [
        { href: '/for-employers', label: strings.forEmployersLink },
        { href: '/free-careers-page', label: strings.freeCareersPageLink },
        { href: '/connector', label: strings.forDevelopers, plain: true },
        { href: '/connector-privacy', label: strings.connectorPrivacy, plain: true },
      ],
    },
  ];

  const widthClass = bleedMargins ? '-mx-2 md:-mx-4 lg:-mx-8' : 'w-full';

  // One palette on every page (theme-aware via jc-* tokens) — same policy as the navbar.
  const headingClass = 'text-sm font-semibold text-jc-ink mb-3';
  const linkClass = 'text-sm text-jc-ink-secondary hover:text-jc-accent hover:underline';
  const emphasisClass = `${linkClass} font-semibold text-jc-accent`;
  const bgClass = 'bg-jc-surface';
  const borderClass = 'border-jc-border';
  const mutedClass = 'text-jc-muted';
  const inkClass = 'text-jc-ink';

  return (
    <footer
      className={`${bgClass} border-t ${borderClass} ${widthClass}`}
      data-footer="true"
      itemScope
      itemType="https://schema.org/WPFooter"
    >
      {/* Zone 1: brand strip */}
      <div className={`px-4 pt-8 pb-6 sm:px-6 lg:px-8 border-b ${borderClass}`}>
        <div className="mx-auto max-w-6xl flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
          <div className="max-w-xl">
            <Link href="/" locale={locale} className={`inline-flex items-center gap-2 font-bold text-lg transition-colors hover:text-jc-accent ${inkClass}`}>
              <img src="/logo.svg" width={24} height={24} alt="" aria-hidden="true" />
              JobCrawls
            </Link>
            <p className={`mt-3 text-sm leading-relaxed ${mutedClass}`}>{strings.brandDescription}</p>
          </div>
          <FooterSocialMediaBar />
        </div>
      </div>

      {/* Zone 2: four columns — desktop grid */}
      <div className={`hidden md:block px-4 py-8 sm:px-6 lg:px-8 border-b ${borderClass}`}>
        <div className="mx-auto max-w-6xl grid grid-cols-4 gap-8">
          {columns.map((col) => (
            <div key={col.key}>
              <h3 className={headingClass}>{col.heading}</h3>
              <FooterColumnLinks
                items={col.items}
                locale={locale}
                linkClassName={linkClass}
                emphasisClassName={emphasisClass}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Zone 2: four columns — mobile accordions, zero-JS */}
      <div className={`md:hidden px-4 sm:px-6 border-b ${borderClass}`}>
        {columns.map((col, i) => (
          <details key={col.key} open={i === 0} className={`border-t first:border-t-0 ${borderClass}`}>
            <summary
              className="list-none cursor-pointer py-3 flex items-center justify-between text-[11.5px] font-bold uppercase tracking-wider text-jc-ink-secondary"
            >
              {col.heading}
              <span aria-hidden className={mutedClass}>
                +
              </span>
            </summary>
            <div className="pb-4">
              <FooterColumnLinks
                items={col.items}
                locale={locale}
                linkClassName={linkClass}
                emphasisClassName={emphasisClass}
              />
            </div>
          </details>
        ))}
      </div>

      {/* Zone 3: language row */}
      <div className={`px-4 py-4 sm:px-6 lg:px-8 border-b ${borderClass}`}>
        <div className="mx-auto max-w-6xl flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm font-semibold">
          <Link href="/jobs" locale="en" className={linkClass}>
            Jobs in English
          </Link>
          <Link href="/jobs" locale="fi" className={linkClass}>
            Työpaikat suomeksi
          </Link>
          <Link href="/jobs" locale="sv" className={linkClass}>
            Lediga jobb på svenska
          </Link>
        </div>
      </div>

      {/* Zone 4: legal bar */}
      <div className={`px-4 py-4 sm:px-6 lg:px-8 border-b ${borderClass}`}>
        <div className={`mx-auto max-w-6xl flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs ${mutedClass}`}>
          <span className={`font-semibold ${inkClass}`}>
            {strings.copyrightBefore}
            <Link href="/about" locale={locale} className={`hover:underline ${inkClass}`}>
              TechTactics Oy
            </Link>
            {strings.copyrightAfter}
          </span>
          <span>
            {strings.businessIdLabel} {BUSINESS_ID}
          </span>
          <Link href="/privacy" locale={locale} className={`${linkClass} font-normal`}>
            {strings.privacy}
          </Link>
          <Link href="/en/terms" locale="en" className={`${linkClass} font-normal`}>
            {strings.terms}
          </Link>
          <button
            type="button"
            onClick={() => {
              if (typeof document !== 'undefined') {
                document.dispatchEvent(new CustomEvent(JC_COOKIE_CONSENT_OPEN_EVENT));
              }
            }}
            className={`${linkClass} font-normal bg-transparent border-none cursor-pointer p-0 text-xs`}
          >
            {strings.cookieSettings}
          </button>
          <Link href="/accessibility-statement" locale={locale} className={`${linkClass} font-normal`}>
            {strings.accessibilityStatementLink}
          </Link>
          <Link href="/contact" locale={locale} className={`${linkClass} font-normal`}>
            {strings.contact}
          </Link>
          <Link href="/sitemap" locale={locale} className={`${linkClass} font-normal`}>
            {strings.sitemap}
          </Link>
        </div>
      </div>

      {/* Zone 5: AI translation disclaimer, demoted to fine print */}
      <div className="px-4 pb-4 sm:px-6 lg:px-8">
        <p className={`text-center text-[10px] italic ${mutedClass}`}>{strings.aiTranslationDisclaimer}</p>
      </div>
    </footer>
  );
}

const MemoizedFooterContent = React.memo(FooterContent);

export interface FooterProps {
  /** Locale for links. When omitted, uses pageContext.locale. */
  locale?: string;
  /** Pre-computed footer strings (SSR). When omitted, uses FooterStringsContext. */
  strings?: FooterStaticStrings;
  /** When true, negative horizontal margins bleed the footer past padded parents. Default false so footers inside overflow-x-hidden shells are not clipped. */
  bleedMargins?: boolean;
}

function Footer({ locale: localeProp, strings: stringsProp, bleedMargins = false }: FooterProps = {}) {
  const pageContext = usePageContext();
  const stringsFromContext = useFooterStrings();

  const locale = useMemo(() => {
    if (localeProp) return localeProp;
    const pc = pageContext as {
      locale?: string;
      urlPathname?: string;
      urlParsed?: { pathname?: string };
    };
    return resolvePageLocale({
      locale: pc?.locale,
      urlPathname: pc?.urlPathname ?? pc?.urlParsed?.pathname,
    });
  }, [localeProp, pageContext]);

  const fallbackStrings = useMemo(
    () => buildFooterStaticStrings(loadLocaleData(locale)),
    [locale]
  );

  const strings = stringsProp ?? stringsFromContext ?? fallbackStrings;

  return <MemoizedFooterContent locale={locale} strings={strings} bleedMargins={bleedMargins} />;
}

export default Footer;
